/**
 * @file index.tsx
 *
 * @fileoverview Creates a layour for recruiters with a side navigation.
 */
import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Footer from '~/jobiqo/components/Footer';
import Header from '~/jobiqo/components/Header';
import { LayoutMainError } from '~/jobiqo/components/Layout/components/LayoutMainError';
import { LayoutContext, LayoutContextInterface } from '~/jobiqo/context/layout';
import RecruiterSidebar from '~/jobiqo/components/Layout/Recruiter/components/RecruiterSidebar';
import { MainWithSideBlock } from '~/vcl/atoms/06-layout/09-main/01-main-width-sideblock';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import Main from '~/vcl/atoms/06-layout/09-main';

const Messages = dynamic(() => import('~/jobiqo/components/Messages'), {
  ssr: false,
});

interface RecruiterLayoutProps {
  /**
   * The content to display inside the layout container.
   */
  children: React.ReactNode;
  /**
   * True if the main content should be full width.
   */
  fullWidthContainer?: boolean;
  /**
   * Main content size.
   */
  mainContentSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * Fixed header.
   *
   * @default false.
   *
   */
  fixedHeader?: boolean;
  /**
   * Hide the site header while keeping the footer visible.
   *
   * @default false.
   */
  hideHeader?: boolean;
}

export const RecruiterLayout = ({
  children,
  fullWidthContainer = false,
  mainContentSize = 'large',
  fixedHeader = false,
  hideHeader = false,
}: RecruiterLayoutProps) => {
  const {
    toggleSidenavCollapsed,
    getSidenavCollapsed,
  }: LayoutContextInterface = useContext(LayoutContext);
  const collapsed = getSidenavCollapsed();

  return (
    <>
      <RecruiterSidebar
        collapsed={collapsed}
        toggleSidenavCollapsed={toggleSidenavCollapsed}
      />
      {fullWidthContainer ? (
        <MainWithSideBlock sideBlockCollapsed={collapsed}>
          {!hideHeader && <Header fixed={fixedHeader} />}
          <main>
            <Messages fullWidth={true} />
            <ErrorBoundary fallback={<LayoutMainError />}>
              {children}
            </ErrorBoundary>
          </main>
          <Footer />
        </MainWithSideBlock>
      ) : (
        <MainWithSideBlock sideBlockCollapsed={collapsed}>
          {!hideHeader && <Header fixed={fixedHeader} />}
          <Spacing size="large" />
          <main>
            <Main size={mainContentSize}>
              <Spacing size="large" />
              <Messages size={mainContentSize} />
              <Spacing size="large" />
              <ErrorBoundary fallback={<LayoutMainError />}>
                {children}
              </ErrorBoundary>
            </Main>
          </main>
          <Spacing marginTop={[8, 12]} />
          <Footer />
        </MainWithSideBlock>
      )}
    </>
  );
};

export default RecruiterLayout;
